import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { Collapse } from "antd";
import { PlusOutlined, MinusOutlined } from "@ant-design/icons";
import { device } from "../../lib/breakpoints";
import { marketingSectionHeading } from "../../styles/marketingSectionTypography";
import { APP_NAME } from "../../helpers/constants";
import { HOME_PAGE_FAQS } from "../../helpers/homePageFaqs";

const { Panel } = Collapse;

const IND_ORANGE = "#FF5722";
const SLATE_800 = "#1e293b";
const SLATE_700 = "#334155";
const WHITE = "#ffffff";
const SLATE_50 = "#F8FAFC";

const FULL_OS_FAQS = [
  {
    q: `What is the ${APP_NAME} Rental Command Center?`,
    a:
      "It is the paid Scale tier: rent through Stripe, simple books inside the app, maintenance tools, and reports for taxes and investors. You can start with contractor search only and add this later.",
  },
  {
    q: "Do I need Scale to use contractor search?",
    a:
      "No. The contractor search works on its own. Scale adds rent collection, ledgers, and deeper reporting when you want them in the same account.",
  },
  {
    q: "How does rent collection work on Scale?",
    a:
      "You connect Stripe, set due dates and amounts per unit, and tenants can pay by card or bank transfer. Card and bank fees follow your Stripe settings.",
  },
  {
    q: "Can I export data for my accountant?",
    a:
      "Yes. You can export categorized expenses and rent history. We do not file taxes for you; we give you clean records to hand off.",
  },
  {
    q: "Is payment data secure?",
    a:
      "Card and bank details stay in Stripe. We only keep what we need to show balances and history in your dashboard.",
  },
];

const FAQ_SUBHEADERS = {
  home: "Plain answers about pricing and how the service works.",
  fullOs: `Rent, books, and reports on ${APP_NAME} (Scale).`,
};

/**
 * @param {{ variant?: 'home' | 'fullOs' }} props
 */
const FAQSection = ({ variant = "home" }) => {
  const [activeKeys, setActiveKeys] = useState([]);

  const faqs = useMemo(
    () => (variant === "fullOs" ? FULL_OS_FAQS : HOME_PAGE_FAQS),
    [variant]
  );
  const subheader = FAQ_SUBHEADERS[variant] ?? FAQ_SUBHEADERS.home;

  return (
    <Section aria-labelledby="faq-heading">
      <Header id="faq-heading">Frequently asked questions</Header>
      <Subheader>{subheader}</Subheader>
      <AccordionContainer>
        <StyledCollapse
          activeKey={activeKeys}
          onChange={setActiveKeys}
          expandIcon={({ isActive }) =>
            isActive ? (
              <MinusOutlined style={{ color: IND_ORANGE, fontSize: "15px" }} />
            ) : (
              <PlusOutlined style={{ color: IND_ORANGE, fontSize: "15px" }} />
            )
          }
          expandIconPosition="end"
        >
          {faqs.map((faq, index) => (
            <Panel header={faq.q} key={`${variant}-${index}`}>
              <AnswerText>{faq.a}</AnswerText>
            </Panel>
          ))}
        </StyledCollapse>
      </AccordionContainer>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        }}
      />
    </Section>
  );
};

export default FAQSection;

const Section = styled.section`
  padding: 4rem 2rem 4.5rem;
  text-align: center;
  box-sizing: border-box;
  background: ${WHITE};
  border-top: 2px solid ${SLATE_800};
  width: 100%;

  @media ${device.mobile} {
    padding: 2.75rem 1rem 3rem;
  }
`;

const Header = styled.h2`
  ${marketingSectionHeading}
  margin-bottom: 0.5rem;
  color: ${SLATE_800};
  font-weight: 800;
  letter-spacing: -0.02em;
  text-transform: none;
`;

const Subheader = styled.p`
  font-size: 1rem;
  font-weight: 400;
  color: ${SLATE_700};
  margin: 0 auto 2.25rem;
  max-width: 36rem;
  font-family: "Inter", system-ui, sans-serif;
  line-height: 1.55;

  @media ${device.mobile} {
    font-size: 0.95rem;
    margin-bottom: 1.75rem;
  }
`;

const AccordionContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: left;
`;

const StyledCollapse = styled(Collapse)`
  background: ${WHITE};
  border-radius: 0;
  border: 2px solid ${SLATE_800};

  .ant-collapse-item {
    border-bottom: 2px solid ${SLATE_800};

    &:last-child {
      border-bottom: none;
    }
  }

  .ant-collapse-header {
    padding: 1.15rem 1.25rem !important;
    font-size: 1rem !important;
    font-weight: 600 !important;
    color: ${SLATE_800} !important;
    font-family: "Inter", system-ui, sans-serif !important;
    line-height: 1.45 !important;
    transition: background 100ms steps(2, end) !important;

    &:hover {
      background: ${SLATE_50};
    }

    @media ${device.mobile} {
      padding: 1rem !important;
      font-size: 0.95rem !important;
    }
  }

  .ant-collapse-item-active .ant-collapse-header {
    background: ${SLATE_50};
  }

  .ant-collapse-content {
    background: ${WHITE};

    .ant-collapse-content-box {
      padding: 0.2rem 1.25rem 1.25rem 1.25rem !important;

      @media ${device.mobile} {
        padding: 0.2rem 1rem 1.1rem 1rem !important;
      }
    }
  }
`;

const AnswerText = styled.div`
  font-size: 0.95rem;
  color: ${SLATE_700};
  font-family: "Inter", system-ui, sans-serif;
  font-weight: 400;
  line-height: 1.65;
  white-space: pre-line;

  strong {
    color: ${SLATE_800};
    font-weight: 600;
  }
`;
